"use client";

import { createContext } from "react";

/**
 * Callback for subscribing to visibility map changes.
 */
export type VisibilitySubscriber = (
  visibilityMap: Record<string, boolean>,
) => void;

/**
 * State for tracking visibility of form inputs within a Form.
 */
export type InputVisibilityState = {
  /**
   * Register or update the visibility state of a form input.
   * @param id - The unique identifier of the form input.
   * @param visible - Whether the form input is visible.
   */
  registerInputVisibility: (id: string, visible: boolean) => void;
  /**
   * Unregister a form input from visibility tracking (typically on unmount).
   * @param id - The unique identifier of the form input.
   */
  unregisterInputVisibility: (id: string) => void;
  /**
   * Subscribe to visibility map changes.
   * @param callback - Function called when visibility map changes.
   * @returns Unsubscribe function.
   */
  subscribeToVisibilityChanges: (callback: VisibilitySubscriber) => () => void;
  /**
   * Get the current visibility map snapshot.
   */
  getVisibilitySnapshot: () => Record<string, boolean>;
};

/**
 * Context for tracking form input visibility within a FormTemplate.
 * This allows FormInputTemplate components to register their visibility state
 * so parent components (like FieldSetTemplate) can determine if they should be shown or hidden.
 */
export const InputVisibilityContext = createContext<
  InputVisibilityState | undefined
>(undefined);
